import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, User, Building2, CreditCard, Bell, Shield, 
  Mail, Phone, MapPin, Save, Eye, EyeOff
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { PartnerLayout } from '@/components/PartnerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';

const PartnerSettings = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isPartner, user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  
  // Profile form state
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    phone: ''
  });
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    email_reservations: true,
    email_bookings: true,
    email_inquiries: true,
    email_marketing: false
  });

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    if (!isPartner) {
      navigate('/dashboard');
      return;
    }
    
    fetchData();
  }, [isAuthenticated, isPartner, authLoading]);

  const fetchData = async () => {
    try {
      // Fetch venues for this partner
      const dashboardRes = await api.get('/partner/dashboard');
      const partnerVenues = dashboardRes.data?.venues || [];
      setVenues(partnerVenues);
      if (partnerVenues.length > 0) {
        setSelectedVenue(partnerVenues[0]);
      }
      
      // Fetch Stripe status
      try {
        const stripeRes = await api.get('/onboarding/stripe/account-status');
        setStripeStatus(stripeRes.data);
      } catch (error) {
        console.log('No Stripe account yet');
        setStripeStatus({ has_account: false, onboarding_complete: false });
      }
      
      // Set profile from user context
      setProfile({
        full_name: user?.full_name || '',
        email: user?.email || '',
        phone: user?.phone || ''
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStripeSetup = async () => {
    setStripeLoading(true);
    try {
      // First check if account exists, if not create one
      if (!stripeStatus?.has_account) {
        await api.post('/onboarding/stripe/create-account');
      }
      
      // Get the onboarding link
      const baseUrl = window.location.origin;
      const response = await api.post('/onboarding/stripe/onboarding-link', null, {
        params: { return_url: `${baseUrl}/partner/settings?tab=billing` }
      });
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Stripe setup error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start Stripe setup');
    } finally {
      setStripeLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const response = await api.get('/onboarding/stripe/dashboard-link');
      window.open(response.data.url, '_blank');
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      // In a real app, this would call an API to update the profile
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setSaving(true);
    try {
      // In a real app, this would call an API to change the password
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated || !isPartner) return null;

  return (
    <PartnerLayout
      title="Settings"
      subtitle="Manage your account and venue settings"
    >
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList>
            <TabsTrigger value="profile" data-testid="settings-tab-profile">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="settings-tab-security">
              <Shield className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="settings-tab-notifications">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="billing" data-testid="settings-tab-billing">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Update your personal details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      value={profile.full_name}
                      onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                      data-testid="settings-full-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profile.email}
                      disabled
                      className="bg-muted"
                      data-testid="settings-email"
                    />
                    <p className="text-xs text-muted-foreground">Contact support to change your email</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      data-testid="settings-phone"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button 
                    onClick={handleSaveProfile} 
                    disabled={saving}
                    data-testid="save-profile-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Venue Info */}
            {selectedVenue && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Venue Information
                  </CardTitle>
                  <CardDescription>Manage your venue details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Venue Name</Label>
                      <Input value={selectedVenue.name} disabled className="bg-muted" />
                    </div>
                    <div className="space-y-2">
                      <Label>Venue Type</Label>
                      <Input value={selectedVenue.venue_type || 'N/A'} disabled className="bg-muted" />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Address</Label>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <Input value={selectedVenue.address || 'N/A'} disabled className="bg-muted" />
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    To update venue information, please contact support or submit a venue update request.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>Update your password to keep your account secure</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4 max-w-md">
                  <div className="space-y-2">
                    <Label htmlFor="current_password">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="current_password"
                        type={showPasswords ? 'text' : 'password'}
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                        data-testid="current-password"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPasswords(!showPasswords)}
                      >
                        {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                      data-testid="new-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                      data-testid="confirm-password"
                    />
                  </div>
                  <Button 
                    onClick={handleChangePassword} 
                    disabled={saving || !passwordForm.current_password || !passwordForm.new_password}
                    data-testid="change-password-btn"
                  >
                    {saving ? 'Updating...' : 'Update Password'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Email Notifications</CardTitle>
                <CardDescription>Choose which notifications you receive</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">New Reservations</p>
                    <p className="text-sm text-muted-foreground">Get notified when someone makes a reservation</p>
                  </div>
                  <Switch
                    checked={notifications.email_reservations}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, email_reservations: checked })}
                    data-testid="notif-reservations"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Ticket Sales</p>
                    <p className="text-sm text-muted-foreground">Get notified for new ticket purchases</p>
                  </div>
                  <Switch
                    checked={notifications.email_bookings}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, email_bookings: checked })}
                    data-testid="notif-bookings"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Private Hire Inquiries</p>
                    <p className="text-sm text-muted-foreground">Get notified for new inquiry submissions</p>
                  </div>
                  <Switch
                    checked={notifications.email_inquiries}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, email_inquiries: checked })}
                    data-testid="notif-inquiries"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Marketing & Tips</p>
                    <p className="text-sm text-muted-foreground">Receive tips to grow your business</p>
                  </div>
                  <Switch
                    checked={notifications.email_marketing}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, email_marketing: checked })}
                    data-testid="notif-marketing"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Stripe Connect</CardTitle>
                <CardDescription>Manage your payout settings and view your Stripe dashboard</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {stripeStatus?.onboarding_complete ? (
                  <>
                    <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-400 mb-2">
                        <CreditCard className="w-5 h-5" />
                        <span className="font-medium">Stripe Account Connected</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Your Stripe account is connected and ready to receive payouts.
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full sm:w-auto"
                      onClick={openStripeDashboard}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Open Stripe Dashboard
                    </Button>
                  </>
                ) : stripeStatus?.has_account ? (
                  <>
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400 mb-2">
                        <CreditCard className="w-5 h-5" />
                        <span className="font-medium">Setup Incomplete</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Your Stripe account was created but onboarding is not complete. Please finish setup to receive payouts.
                      </p>
                    </div>
                    <Button 
                      className="w-full sm:w-auto bg-yellow-600 hover:bg-yellow-700"
                      onClick={handleStripeSetup}
                      disabled={stripeLoading}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {stripeLoading ? 'Loading...' : 'Complete Stripe Setup'}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
                      <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400 mb-2">
                        <CreditCard className="w-5 h-5" />
                        <span className="font-medium">Stripe Not Connected</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Connect your Stripe account to receive automatic payouts for your sales.
                      </p>
                    </div>
                    <Button 
                      className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700"
                      onClick={handleStripeSetup}
                      disabled={stripeLoading}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {stripeLoading ? 'Loading...' : 'Connect Stripe Account'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Fee Structure</CardTitle>
                <CardDescription>Platform fees applied to your sales</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-indigo-600">3.5% + $1.59</p>
                    <p className="text-sm font-medium mt-1">Event Tickets</p>
                    <p className="text-xs text-muted-foreground mt-1">Service fee added to ticket price (paid by customer)</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">25%</p>
                    <p className="text-sm font-medium mt-1">Experiences</p>
                    <p className="text-xs text-muted-foreground mt-1">Commission deducted from sale price</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </PartnerLayout>
  );
};

export default PartnerSettings;
